import React, {useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';

import {ButtonLink} from '@homeaway/react-buttons';
import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';

import SelectedFilterPill from '../SelectedFilterPill/SelectedFilterPill';
import {
    triggerEdapEventForClearAllFilters,
    triggerEdapEventForSelectedFilterPillRemoval,
} from '../../utils/edap';
import {getPriceRangeFilterPillLabel} from '../../utils/filterPillUtils';
import {
    getBedroomLabel,
    getBedroomValue,
    getBathroomLabel,
    getBathroomValue,
} from '../../utils/roomsAndSpacesUtils';
import {
    PRICE_FILTER_GROUP_ID,
    POPULAR_FILTER_GROUP_ID,
    ROOMSANDSPACES_FILTER_GROUP_ID,
    PRICE_FILTER_ID_FOR_EDAP_EVENTING,
    BEDROOMS_FILTER_ID_FOR_EDAP_EVENTING,
    BATHROOMS_FILTER_ID_FOR_EDAP_EVENTING,
} from '../../constants/filters';

type Filter = {
    description?: string;
    refineByQueryArgument: string;
    id: string;
    groupId?: string;
    name: string;
};

type Refinement = {
    count?: number;
    checked: boolean;
    filter: Filter;
};

type FilterGroupInfo = {
    id: string;
    name: string;
};

type FilterGroupType = {
    groupInfo: FilterGroupInfo;
    filters: Refinement[];
};

type CustomFilterParams = {
    selectedFilterByTotalPrice: boolean;
    appliedFilterByTotalPrice: boolean;
    appliedMaxNightlyPrice: number | null;
    appliedMinNightlyPrice: number | null;
    selectedMaxNightlyPrice: number | null;
    selectedMinNightlyPrice: number | null;
    selectedMaxTotalPrice: number | null;
    selectedMinTotalPrice: number | null;
    appliedMaxTotalPrice: number | null;
    appliedMinTotalPrice: number | null;
    selectedMinBedrooms: number;
    selectedMinBathrooms: number;
    appliedMinBedrooms: number;
    appliedMinBathrooms: number;
};

type Props = {
    selectedFilters: string[];
    customFiltersParams: CustomFilterParams;
    filterGroups: FilterGroupType[];
    currency: string;
    lengthOfStay?: number;
    onCustomFilterUpdate: Function;
};

function isSomePriceFilterSelected(
    customFiltersParams: CustomFilterParams
): boolean {
    return (
        (customFiltersParams !== null &&
            customFiltersParams.selectedMinNightlyPrice !== null &&
            customFiltersParams.selectedMinNightlyPrice > 0) ||
        (customFiltersParams.selectedMinTotalPrice !== null &&
            customFiltersParams.selectedMinTotalPrice > 0) ||
        customFiltersParams.selectedMaxNightlyPrice !== null ||
        customFiltersParams.selectedMaxTotalPrice !== null
    );
}

export default function SelectedFiltersGroup({
    selectedFilters,
    customFiltersParams,
    filterGroups,
    currency,
    lengthOfStay,
    onCustomFilterUpdate,
}: Props) {
    const edapContext: TrackingContextValue = useContext(TrackingContext);
    const {formatMessage, locale} = useIntl();
    const selectedFiltersSet = new Set(selectedFilters);

    const {refinementsInRankedOrder, filterIdsToGroupIdMap} = useMemo(() => {
        const rankedRefinementArray: Refinement[] = [];
        const filterToGroupIdMap = new Map();
        filterGroups.forEach((filterGroupType) => {
            // Ignore the "Popular" filter group so we don't process duplicate data
            if (filterGroupType.groupInfo.id !== POPULAR_FILTER_GROUP_ID) {
                filterGroupType.filters.forEach((refinement) => {
                    rankedRefinementArray.push(refinement);
                    filterToGroupIdMap.set(
                        refinement.filter.id,
                        filterGroupType.groupInfo.id
                    );
                });
            }
        });
        return {
            refinementsInRankedOrder: rankedRefinementArray,
            filterIdsToGroupIdMap: filterToGroupIdMap,
        };
    }, filterGroups);

    const removeSelectedFilterFromArray = (
        selectedFilterArray,
        selectedFilter
    ) => {
        return selectedFilterArray.reduce((total, accum) => {
            if (accum !== selectedFilter) {
                total.push(accum);
            }
            return total;
        }, []);
    };

    const handleOnPillClick = (updatedFilters, filterId, filterGroupId) => {
        onCustomFilterUpdate({
            ...updatedFilters,
        });
        triggerEdapEventForSelectedFilterPillRemoval(
            filterId,
            filterGroupId,
            edapContext
        );
    };

    const handleOnClearAllClick = (updatedFilters) => {
        onCustomFilterUpdate({
            ...updatedFilters,
        });
        triggerEdapEventForClearAllFilters(edapContext);
    };

    const selectedRefinementsInRankedOrder = refinementsInRankedOrder.filter(
        (refinement) =>
            selectedFiltersSet.has(refinement.filter.refineByQueryArgument)
    );

    const somePriceFilterSelected = isSomePriceFilterSelected(
        customFiltersParams
    );

    let priceFilterRangeLabel;
    if (somePriceFilterSelected) {
        priceFilterRangeLabel = getPriceRangeFilterPillLabel({
            defaultLabel: 'Price',
            currency,
            locale,
            filterByTotalPrice: customFiltersParams.selectedFilterByTotalPrice,
            minTotalPrice: customFiltersParams.selectedMinTotalPrice,
            minNightlyPrice: customFiltersParams.selectedMinNightlyPrice,
            maxTotalPrice: customFiltersParams.selectedMaxTotalPrice,
            maxNightlyPrice: customFiltersParams.selectedMaxNightlyPrice,
            lengthOfStay,
        });
    }

    let minBedroomsFilterLabel;
    const minBedroomsFilterSelected =
        customFiltersParams.selectedMinBedrooms > 0;
    if (minBedroomsFilterSelected) {
        minBedroomsFilterLabel = `${getBedroomValue(
            customFiltersParams.selectedMinBedrooms,
            formatMessage
        )} ${getBedroomLabel(formatMessage)}`;
    }

    let minBathroomsFilterLabel;
    const minBathroomsFilterSelected =
        customFiltersParams.selectedMinBathrooms > 0;
    if (minBathroomsFilterSelected) {
        minBathroomsFilterLabel = `${getBathroomValue(
            customFiltersParams.selectedMinBathrooms,
            formatMessage
        )} ${getBathroomLabel(formatMessage)}`;
    }

    const atLeastOneFilterIsSelected =
        somePriceFilterSelected ||
        minBedroomsFilterSelected ||
        minBathroomsFilterSelected ||
        selectedFiltersSet.size !== 0;

    return atLeastOneFilterIsSelected ? (
        <div className="MoreFiltersModal__filterGroup">
            <div className="MoreFiltersModal__yourSelections">
                <span className="MoreFiltersModal__yourSelections--label">
                    {formatMessage({
                        id: 'yourSelections',
                        defaultMessage: 'Your selections',
                    })}
                </span>
                <ButtonLink
                    className="MoreFiltersModal__button--clear"
                    id="filter-group-clear"
                    label={formatMessage({
                        id: 'clearAll',
                        defaultMessage: 'Clear all',
                    })}
                    size="sm"
                    onClick={() => {
                        handleOnClearAllClick({
                            minTotalPrice: 0,
                            minNightlyPrice: 0,
                            maxTotalPrice: null,
                            maxNightlyPrice: null,
                            minBedrooms: 0,
                            minBathrooms: 0,
                            filter: [],
                        });
                    }}
                />
            </div>
            {somePriceFilterSelected && (
                <SelectedFilterPill
                    onClick={() => {
                        handleOnPillClick(
                            {
                                minTotalPrice: 0,
                                minNightlyPrice: 0,
                                maxTotalPrice: null,
                                maxNightlyPrice: null,
                            },
                            PRICE_FILTER_ID_FOR_EDAP_EVENTING,
                            PRICE_FILTER_GROUP_ID
                        );
                    }}
                    label={priceFilterRangeLabel}
                    key={'priceRange'}
                />
            )}
            {minBedroomsFilterSelected && (
                <SelectedFilterPill
                    onClick={() => {
                        handleOnPillClick(
                            {
                                minBedrooms: 0,
                            },
                            BEDROOMS_FILTER_ID_FOR_EDAP_EVENTING,
                            ROOMSANDSPACES_FILTER_GROUP_ID
                        );
                    }}
                    label={minBedroomsFilterLabel}
                    key={'minBedrooms'}
                />
            )}
            {minBathroomsFilterSelected && (
                <SelectedFilterPill
                    onClick={() => {
                        handleOnPillClick(
                            {
                                minBathrooms: 0,
                            },
                            BATHROOMS_FILTER_ID_FOR_EDAP_EVENTING,
                            ROOMSANDSPACES_FILTER_GROUP_ID
                        );
                    }}
                    label={minBathroomsFilterLabel}
                    key={'minBathrooms'}
                />
            )}
            {selectedRefinementsInRankedOrder.map((selectedRefinement) => (
                <SelectedFilterPill
                    onClick={() => {
                        handleOnPillClick(
                            {
                                filter: removeSelectedFilterFromArray(
                                    selectedFilters,
                                    selectedRefinement.filter
                                        .refineByQueryArgument
                                ),
                            },
                            selectedRefinement.filter.id,
                            filterIdsToGroupIdMap.get(
                                selectedRefinement.filter.id
                            )
                        );
                    }}
                    label={selectedRefinement.filter.name}
                    key={selectedRefinement.filter.id}
                />
            ))}
        </div>
    ) : null;
}
